import React, {useRef, useEffect} from 'react';
import {BOT_CONV_MESSAGE_TYPE} from '../../consts/common-consts';
import BotMessageFactory from '../messenger-elements/bot-message/bot-message-factory'
import MessageTrace from '../message-trace';

/**
 * Round avatar beside a message. Sized to line up with the first line of the
 * bubble rather than the middle of a multi-line one.
 */
function Avatar({isBot}) {
  const theme = isBot
    ? 'bg-gray-100 text-gray-500'
    : 'bg-indigo-50 text-indigo-600';

  return (
    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${theme}`}>
      {isBot ? (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )}
    </div>
  );
}

/**
 * One message row.
 *
 * Bot and operator differ only in which side they sit on and which avatar they
 * carry, so both cases share this layout instead of the two near-identical
 * components that used to duplicate it - the earlier pair had already drifted
 * (different vertical margins, only one showing a trace).
 */
function MessageRow(props) {
  const {senderType, traceGroup, traceLoading} = props;
  const isBot = senderType === BOT_CONV_MESSAGE_TYPE;

  return (
    <div className={`flex w-full gap-2 px-4 py-1.5 ${isBot ? '' : 'flex-row-reverse'}`}>
      <Avatar isBot={isBot} />

      <div className={`flex min-w-0 flex-col ${isBot ? 'items-start' : 'items-end'}`}>
        <span className='px-1 pb-0.5 text-[11px] font-medium text-gray-400'>
          {isBot ? 'Bot' : 'You'}
        </span>

        <BotMessageFactory {...props} />

        {/* The turn this message triggered. Only user messages carry one - the
            spans describe the work their message set off. */}
        {!isBot && (traceGroup || traceLoading) && (
          <MessageTrace group={traceGroup} loading={traceLoading} />
        )}
      </div>
    </div>
  );
}

export default function MessengerContainer(props) {
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({behavior: 'smooth', block: 'end'});
    }
  }, []);

  return (
    <div ref={ref}>
      <MessageRow {...props} />
    </div>
  );
}
